import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Loader2, User, Mail, Phone, MapPin, Briefcase, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { toast } from '../../components/ui/use-toast';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

interface UserProfile {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  position?: string;
  city?: string;
  mobile_number?: string;
  locale?: string;
  manager_id?: number;
  manager?: { id: number; first_name: string; last_name: string };
}

interface PersonalInfoSectionProps {
  profile: UserProfile;
  onProfileUpdated: (user: UserProfile) => void;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({ profile, onProfileUpdated }) => {
  const { t } = useTranslation('profile');
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    position: '',
    city: '',
    mobile_number: '',
    locale: 'en',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      position: profile.position || '',
      city: profile.city || '',
      mobile_number: profile.mobile_number || '',
      locale: profile.locale || 'en',
    });
  }, [profile]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body: Record<string, string | number | null> = {
        first_name: form.first_name,
        last_name: form.last_name,
        position: form.position || null,
        city: form.city || null,
        mobile_number: form.mobile_number || null,
        locale: form.locale,
      };

      const res = await fetch(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        throw new Error(data.message || 'Failed to update profile');
      }

      onProfileUpdated(data.user);
      toast({ title: 'Profile updated', description: 'Your personal information has been saved.', variant: 'success' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update profile', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="bg-slate-50 border-b">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <User className="h-5 w-5" /> {t('personal_information')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-6">
        {/* Email (read-only) */}
        <div className="space-y-1.5">
          <Label htmlFor="email" className="flex items-center gap-1.5 text-gray-600">
            <Mail className="h-3.5 w-3.5 text-slate-400" /> {t('email')}
          </Label>
          <Input id="email" value={profile.email} disabled className="bg-gray-50 text-gray-500" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="first_name" className="flex items-center gap-1.5 text-gray-600">
              <User className="h-3.5 w-3.5 text-slate-400" /> {t('first_name')}
            </Label>
            <Input
              id="first_name"
              value={form.first_name}
              onChange={e => handleChange('first_name', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last_name" className="flex items-center gap-1.5 text-gray-600">
              <User className="h-3.5 w-3.5 text-slate-400" /> {t('last_name')}
            </Label>
            <Input
              id="last_name"
              value={form.last_name}
              onChange={e => handleChange('last_name', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="mobile_number" className="flex items-center gap-1.5 text-gray-600">
              <Phone className="h-3.5 w-3.5 text-slate-400" /> {t('mobile_number')}
            </Label>
            <Input
              id="mobile_number"
              value={form.mobile_number}
              onChange={e => handleChange('mobile_number', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="city" className="flex items-center gap-1.5 text-gray-600">
              <MapPin className="h-3.5 w-3.5 text-slate-400" /> {t('city')}
            </Label>
            <Input
              id="city"
              value={form.city}
              onChange={e => handleChange('city', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="position" className="flex items-center gap-1.5 text-gray-600">
              <Briefcase className="h-3.5 w-3.5 text-slate-400" /> {t('position_job_title')}
            </Label>
            <Input
              id="position"
              value={form.position}
              onChange={e => handleChange('position', e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="locale" className="flex items-center gap-1.5 text-gray-600">
              <Globe className="h-3.5 w-3.5 text-slate-400" /> {t('language')}
            </Label>
            <Select value={form.locale} onValueChange={(val) => handleChange('locale', val)}>
              <SelectTrigger id="locale">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving} >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('save_changes')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalInfoSection;
